import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import ReactFlow, { Background, Controls, type Edge, type Node } from "reactflow";
import "reactflow/dist/style.css";
import { Landing } from "./Landing.js";

/**
 * DrChief v2 web UI — minimal but functional. Three views:
 *   1. Login — calls /api/auth/login, stashes nothing client-side (httponly cookie).
 *   2. Sessions (when authenticated) — opens new session, lists known runs.
 *   3. Live stream — WS connection to /api/sessions/:id/stream + DAG status.
 *
 * The xyflow-based DAG visualizer + memory-tree dendrogram are M3 polish;
 * for M2 we list nodes vertically. Tailwind / shadcn / etc. are deferred
 * to keep the bundle tiny — plain CSS variables in styles.css cover both
 * dark and light schemes.
 */

interface User {
  username: string;
  role: string;
  mustChangePassword: boolean;
}

interface LocalFileHandle {
  kind: "file";
  name: string;
  getFile(): Promise<File>;
  createWritable(): Promise<{ write(data: Blob | string | ArrayBuffer): Promise<void>; close(): Promise<void> }>;
}

interface LocalDirectoryHandle {
  kind: "directory";
  name: string;
  values(): AsyncIterable<LocalFileHandle | LocalDirectoryHandle>;
  getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<LocalDirectoryHandle>;
  getFileHandle(name: string, options?: { create?: boolean }): Promise<LocalFileHandle>;
  removeEntry(name: string, options?: { recursive?: boolean }): Promise<void>;
  queryPermission?(descriptor?: { mode?: "read" | "readwrite" }): Promise<PermissionState>;
  requestPermission?(descriptor?: { mode?: "read" | "readwrite" }): Promise<PermissionState>;
}

type DirectoryPicker = (options?: { mode?: "read" | "readwrite" }) => Promise<LocalDirectoryHandle>;

type View = "loading" | "landing" | "login" | "change-password" | "workspace" | "sessions" | "stream";

interface SessionStatusEvent {
  kind: string;
  [key: string]: unknown;
}

export default function App() {
  const [view, setView] = useState<View>("loading");
  const [user, setUser] = useState<User | null>(null);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    return (localStorage.getItem("drchief.sidebarOpen") ?? "1") !== "0";
  });

  useEffect(() => {
    localStorage.setItem("drchief.sidebarOpen", sidebarOpen ? "1" : "0");
  }, [sidebarOpen]);

  useEffect(() => {
    fetchMe().then((u) => {
      if (!u) {
        // Unauthenticated visitors see the public landing page first.
        // Clicking "Sign in" flips view → "login". Already-logged-in
        // users skip the landing entirely.
        setView("landing");
      }
      else if (u.mustChangePassword) {
        // mustChangePassword is now advisory in v2 — user can still go
        // straight to Sessions and change later from the header. The
        // dedicated change-password screen still works for users who
        // click "Change password" explicitly.
        setUser(u);
        setView("workspace");
      } else {
        setUser(u);
        setView("workspace");
      }
    });
  }, []);

  function onLogin(u: User) {
    setUser(u);
    setView("workspace");
  }

  function onPasswordChanged() {
    if (user) setUser({ ...user, mustChangePassword: false });
    setView("workspace");
  }

  async function onLogout() {
    await fetch("/api/auth/logout", { method: "POST" });
    setUser(null);
    setActiveSessionId(null);
    setView("login");
  }

  const [activePermissionMode, setActivePermissionMode] = useState<string>("safe");

  function onSessionStarted(sessionId: string, permissionMode: string) {
    setActiveSessionId(sessionId);
    setActivePermissionMode(permissionMode);
    setView("stream");
  }

  function onBackToSessions() {
    setActiveSessionId(null);
    setView("sessions");
  }

  if (view === "loading") return <div className="shell muted">Loading...</div>;
  if (view === "landing") return <Landing onSignIn={() => setView("login")} />;
  if (view === "login") return <Login onLogin={onLogin} setError={setError} />;
  if (view === "change-password") return <ChangePassword onChanged={onPasswordChanged} setError={setError} />;
  if (user && view === "workspace") {
    return (
      <Workspace
        user={user}
        error={error}
        setError={setError}
        onLogout={onLogout}
        onChangePassword={() => setView("change-password")}
      />
    );
  }

  const showShell = user && view !== "login" && view !== "loading" && view !== "landing" && view !== "workspace";

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      {showShell && sidebarOpen && (
        <>
          <SessionSidebar
            activeSessionId={activeSessionId}
            onOpenSession={(sid, mode) => {
              onSessionStarted(sid, mode);
              // Auto-collapse drawer on small screens after navigation.
              if (typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches) {
                setSidebarOpen(false);
              }
            }}
            onNewSession={() => {
              setActiveSessionId(null);
              setView("sessions");
              if (typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches) {
                setSidebarOpen(false);
              }
            }}
            onCollapse={() => setSidebarOpen(false)}
          />
          <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
        </>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        {showShell && (
          <div className="header">
            <div className="row" style={{ gap: 12 }}>
              {!sidebarOpen && (
                <button className="ghost" onClick={() => setSidebarOpen(true)} title="Show sessions sidebar">☰</button>
              )}
              <strong>DrChief v2</strong>
            </div>
            <div className="row">
              <span className="muted">{user.username}</span>
              <button className="ghost" onClick={() => setView("change-password")}>Change password</button>
              <button className="ghost" onClick={onLogout}>Logout</button>
            </div>
          </div>
        )}
        {error && <div className="shell error">{error}</div>}
        {view === "loading" && <div className="shell muted">Loading…</div>}
        {view === "landing" && <Landing onSignIn={() => setView("login")} />}
        {view === "login" && <Login onLogin={onLogin} setError={setError} />}
        {view === "change-password" && <ChangePassword onChanged={onPasswordChanged} setError={setError} />}
        {view === "sessions" && <Sessions onStarted={onSessionStarted} setError={setError} />}
        {view === "stream" && activeSessionId && <Stream sessionId={activeSessionId} onBack={onBackToSessions} permissionMode={activePermissionMode} onSessionStarted={onSessionStarted} />}
      </div>
    </div>
  );
}

interface WorkspaceChatMessage {
  role: "user" | "assistant" | "system";
  content: string;
  time: string;
}

interface WorkspaceChat {
  id: string;
  title: string;
  mode: "quick" | "workflow" | "clarify";
  status: string;
  sessionId?: string;
  updatedAt: string;
  messages: WorkspaceChatMessage[];
}

interface WorkspaceProject {
  id: string;
  name: string;
  folder: string;
  source?: {
    kind: "server-path" | "browser-local";
    localName?: string;
  };
  updatedAt: string;
  chats: WorkspaceChat[];
}

type WorkspacePane = "left" | "main" | "summary";
type DragTarget = "left" | "summary";

const LEFT_WIDTH_KEY = "drchief.workspace.leftWidth";
const SUMMARY_WIDTH_KEY = "drchief.workspace.summaryWidth";
const LEFT_OPEN_KEY = "drchief.workspace.leftOpen";
const MAIN_OPEN_KEY = "drchief.workspace.mainOpen";
const SUMMARY_OPEN_KEY = "drchief.workspace.summaryOpen";
const MIN_LEFT_WIDTH = 180;
const MAX_LEFT_WIDTH = 520;
const MIN_SUMMARY_WIDTH = 280;
const MAX_SUMMARY_WIDTH = 820;

function readStoredBool(key: string, fallback: boolean): boolean {
  const value = localStorage.getItem(key);
  if (value === "1") return true;
  if (value === "0") return false;
  return fallback;
}

function readStoredNumber(key: string, fallback: number, min: number, max: number): number {
  const value = Number(localStorage.getItem(key));
  return Number.isFinite(value) ? clampPanelWidth(value, min, max) : fallback;
}

function clampPanelWidth(value: number, min: number, max: number): number {
  const safeMax = Math.max(min, max);
  return Math.min(safeMax, Math.max(min, Math.round(value)));
}

function Workspace(props: {
  user: User;
  error: string | null;
  setError: (value: string | null) => void;
  onLogout: () => void;
  onChangePassword: () => void;
}) {
  const [projects, setProjects] = useState<WorkspaceProject[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<WorkspaceChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState<"auto" | "quick" | "workflow">("auto");
  const [permissionMode, setPermissionMode] = useState<"extended" | "bypass">("extended");
  const [runSessionId, setRunSessionId] = useState<string | null>(null);
  const [leftOpen, setLeftOpen] = useState(() => readStoredBool(LEFT_OPEN_KEY, true));
  const [mainOpen, setMainOpen] = useState(() => readStoredBool(MAIN_OPEN_KEY, true));
  const [sideOpen, setSideOpen] = useState(() => readStoredBool(SUMMARY_OPEN_KEY, true));
  const [leftWidth, setLeftWidth] = useState(() => readStoredNumber(LEFT_WIDTH_KEY, 300, MIN_LEFT_WIDTH, MAX_LEFT_WIDTH));
  const [summaryWidth, setSummaryWidth] = useState(() => readStoredNumber(SUMMARY_WIDTH_KEY, 390, MIN_SUMMARY_WIDTH, MAX_SUMMARY_WIDTH));
  const [fullscreenPane, setFullscreenPane] = useState<"main" | "summary" | null>(null);
  const [dragging, setDragging] = useState<DragTarget | null>(null);
  const [fileMenuOpen, setFileMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [folderBusy, setFolderBusy] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const localHandlesRef = useRef<Record<string, LocalDirectoryHandle>>({});
  const syncedSessionsRef = useRef<Set<string>>(new Set());
  const sessionProjectsRef = useRef<Record<string, string>>({});

  const activeProject = projects.find((p) => p.id === activeProjectId) ?? projects[0] ?? null;
  const visibleLeft = leftOpen && fullscreenPane === null;
  const visibleMain = mainOpen && (fullscreenPane === null || fullscreenPane === "main");
  const visibleSummary = sideOpen && (fullscreenPane === null || fullscreenPane === "summary");
  const visiblePaneCount = [leftOpen, mainOpen, sideOpen].filter(Boolean).length;
  const showLeftResizer = visibleLeft && !fullscreenPane && (visibleMain || visibleSummary);
  const showSummaryResizer = visibleSummary && !fullscreenPane && (visibleLeft || visibleMain);
  const gridColumns = [
    visibleLeft ? `${leftWidth}px` : null,
    showLeftResizer ? "6px" : null,
    visibleMain ? "minmax(320px, 1fr)" : null,
    showSummaryResizer ? "6px" : null,
    visibleSummary ? (visiblePaneCount === 1 || fullscreenPane === "summary" ? "minmax(0, 1fr)" : `${summaryWidth}px`) : null,
  ].filter(Boolean).join(" ") || "minmax(0, 1fr)";
  const workspaceStyle: CSSProperties = { gridTemplateColumns: gridColumns };
  const workspaceClass = [
    "workspace",
    visibleSummary ? "with-side" : "",
    !visibleLeft ? "left-hidden" : "",
    !visibleMain ? "main-hidden" : "",
    !visibleSummary ? "summary-hidden" : "",
    fullscreenPane ? `fullscreen-${fullscreenPane}` : "",
    dragging ? "is-resizing" : "",
  ].filter(Boolean).join(" ");

  const refreshWorkspace = async () => {
    const r = await fetch("/api/workspace", { credentials: "include" });
    const j = (await r.json()) as { ok?: boolean; projects?: WorkspaceProject[]; error?: string };
    if (!j.ok) {
      props.setError(j.error ?? "Failed to load workspace");
      return;
    }
    const next = j.projects ?? [];
    setProjects(next);
    setActiveProjectId((current) => current ?? next[0]?.id ?? null);
  };

  useEffect(() => {
    refreshWorkspace().catch((e) => props.setError(String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages.length]);

  useEffect(() => localStorage.setItem(LEFT_WIDTH_KEY, String(leftWidth)), [leftWidth]);
  useEffect(() => localStorage.setItem(SUMMARY_WIDTH_KEY, String(summaryWidth)), [summaryWidth]);
  useEffect(() => localStorage.setItem(LEFT_OPEN_KEY, leftOpen ? "1" : "0"), [leftOpen]);
  useEffect(() => localStorage.setItem(MAIN_OPEN_KEY, mainOpen ? "1" : "0"), [mainOpen]);
  useEffect(() => localStorage.setItem(SUMMARY_OPEN_KEY, sideOpen ? "1" : "0"), [sideOpen]);

  useEffect(() => {
    if (!dragging) return;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const onPointerMove = (event: PointerEvent) => {
      if (dragging === "left") {
        setLeftWidth(clampPanelWidth(event.clientX, MIN_LEFT_WIDTH, Math.min(MAX_LEFT_WIDTH, window.innerWidth - 420)));
      } else {
        setSummaryWidth(clampPanelWidth(window.innerWidth - event.clientX, MIN_SUMMARY_WIDTH, Math.min(MAX_SUMMARY_WIDTH, window.innerWidth - 320)));
      }
    };
    const stopDragging = () => setDragging(null);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", stopDragging, { once: true });
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", stopDragging);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [dragging]);

  function setPaneOpen(pane: WorkspacePane, next: boolean) {
    const state = { left: leftOpen, main: mainOpen, summary: sideOpen };
    state[pane] = next;
    if (!state.left && !state.main && !state.summary) return;
    if (pane === "left") setLeftOpen(next);
    if (pane === "main") setMainOpen(next);
    if (pane === "summary") setSideOpen(next);
    if (!next && fullscreenPane === pane) setFullscreenPane(null);
  }

  function toggleFullscreen(pane: "main" | "summary") {
    if (pane === "main") setMainOpen(true);
    if (pane === "summary") setSideOpen(true);
    setFullscreenPane((current) => current === pane ? null : pane);
  }

  function beginResize(target: DragTarget, event: React.PointerEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(target);
  }

  function resizeWithKeyboard(target: DragTarget, event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const direction = event.key === "ArrowRight" ? 1 : -1;
    if (target === "left") {
      setLeftWidth((width) => clampPanelWidth(width + direction * 20, MIN_LEFT_WIDTH, MAX_LEFT_WIDTH));
    } else {
      setSummaryWidth((width) => clampPanelWidth(width - direction * 20, MIN_SUMMARY_WIDTH, MAX_SUMMARY_WIDTH));
    }
  }

  async function openFolder() {
    setFileMenuOpen(false);
    const picker = (window as Window & { showDirectoryPicker?: DirectoryPicker }).showDirectoryPicker;
    if (!picker) {
      props.setError("Browser này chưa hỗ trợ chọn folder local. Hãy dùng Chrome/Edge trên HTTPS hoặc localhost.");
      return;
    }
    props.setError(null);
    setFolderBusy(true);
    setSyncStatus("Waiting for local folder permission...");
    try {
      const handle = await picker({ mode: "readwrite" });
      const allowed = await ensureLocalFolderPermission(handle);
      if (!allowed) {
        props.setError("DrChief cần quyền read/write để sync project local.");
        return;
      }
      setSyncStatus(`Creating server mirror for ${handle.name}...`);
      const r = await fetch("/api/workspace/open-local-folder", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: handle.name }),
      });
      const j = (await r.json()) as { ok?: boolean; project?: WorkspaceProject; error?: string };
      if (!j.ok || !j.project) {
        props.setError(j.error ?? "Open folder failed");
        return;
      }
      localHandlesRef.current[j.project.id] = handle;
      await uploadLocalFolderSnapshot(j.project.id, handle, setSyncStatus);
      await refreshWorkspace();
      setActiveProjectId(j.project.id);
      setActiveChatId(null);
      setMessages([
        {
          role: "system",
          content: `Attached local folder "${handle.name}". Files were synced to the server mirror for agents. Workflow output will sync back while this tab keeps folder permission.`,
          time: new Date().toISOString(),
        },
      ]);
      setRunSessionId(null);
      setSyncStatus(`Local folder attached: ${handle.name}`);
    } catch (e) {
      if ((e as Error).name !== "AbortError") props.setError((e as Error).message);
    } finally {
      setFolderBusy(false);
    }
  }

  function newChat() {
    setActiveChatId(null);
    setMessages([]);
    setRunSessionId(null);
    setInput("");
  }

  function selectChat(projectId: string, chat: WorkspaceChat) {
    setActiveProjectId(projectId);
    setActiveChatId(chat.id);
    setMessages(chat.messages ?? []);
    setRunSessionId(chat.sessionId ?? null);
    if (chat.sessionId) sessionProjectsRef.current[chat.sessionId] = projectId;
    if (chat.sessionId) setSideOpen(true);
  }

  async function submit() {
    const text = input.trim();
    if (!text || busy) return;
    if (!activeProject) {
      await openFolder();
      return;
    }
    props.setError(null);
    setBusy(true);
    const userMessage: WorkspaceChatMessage = { role: "user", content: text, time: new Date().toISOString() };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    try {
      const r = await fetch("/api/workspace/chats", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId: activeProject.id,
          message: text,
          mode,
          permissionMode,
        }),
      });
      const j = (await r.json()) as { ok?: boolean; type?: string; message?: string; sessionId?: string; chat?: WorkspaceChat; error?: string };
      if (!j.ok) {
        props.setError(j.error ?? "Chat failed");
        return;
      }
      setActiveChatId(j.chat?.id ?? null);
      if (j.type === "workflow" && j.sessionId) {
        sessionProjectsRef.current[j.sessionId] = activeProject.id;
        setRunSessionId(j.sessionId);
        setSideOpen(true);
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `Workflow started. Session: ${j.sessionId}`,
            time: new Date().toISOString(),
          },
        ]);
      } else if (j.message) {
        setMessages((prev) => [...prev, { role: "assistant", content: j.message ?? "", time: new Date().toISOString() }]);
      }
      await refreshWorkspace();
    } finally {
      setBusy(false);
    }
  }

  function onComposerKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  }

  async function syncWorkflowBackToLocal(sessionId: string) {
    if (syncedSessionsRef.current.has(sessionId)) return;
    const projectId = sessionProjectsRef.current[sessionId] ?? activeProjectId;
    const handle = projectId ? localHandlesRef.current[projectId] : undefined;
    if (!handle) {
      setSyncStatus("Workflow finished. Re-open the local folder to sync results back to this machine.");
      return;
    }
    syncedSessionsRef.current.add(sessionId);
    try {
      await syncSessionOutputToLocal(sessionId, handle, setSyncStatus);
    } catch (e) {
      props.setError(`Local sync failed: ${(e as Error).message}`);
    }
  }

  return (
    <div className={workspaceClass} style={workspaceStyle}>
      <div className="workspace-layout-toolbar" aria-label="Workspace layout controls">
        <button className={leftOpen ? "active" : ""} onClick={() => setPaneOpen("left", !leftOpen)}>
          {leftOpen ? "Hide sidebar" : "Show sidebar"}
        </button>
        <button className={mainOpen ? "active" : ""} onClick={() => setPaneOpen("main", !mainOpen)}>
          {mainOpen ? "Hide chat" : "Show chat"}
        </button>
        <button className={sideOpen ? "active" : ""} onClick={() => setPaneOpen("summary", !sideOpen)}>
          {sideOpen ? "Hide summary" : "Show summary"}
        </button>
        <button onClick={() => toggleFullscreen("main")}>{fullscreenPane === "main" ? "Exit chat full" : "Chat full"}</button>
        <button onClick={() => toggleFullscreen("summary")}>{fullscreenPane === "summary" ? "Exit summary full" : "Summary full"}</button>
      </div>
      {visibleLeft && <aside className="workspace-sidebar">
        <div className="workspace-menu">
          <div className="menu-root">
            <button className="menu-button" onClick={() => setFileMenuOpen((v) => !v)}>File</button>
            {fileMenuOpen && (
              <div className="file-menu">
                <button onClick={openFolder} disabled={folderBusy}>Open local folder...</button>
              </div>
            )}
          </div>
          <button className="icon-button" onClick={newChat} title="New chat">+</button>
          <button className="icon-button" onClick={() => setPaneOpen("left", false)} title="Hide sidebar">Hide</button>
        </div>
        <button className="nav-action" onClick={newChat}>New chat</button>
        <button className="nav-action" onClick={openFolder} disabled={folderBusy}>Open local folder</button>
        <div className="workspace-section-title">Projects</div>
        <div className="project-list">
          {projects.length === 0 && <div className="empty-state">No folders opened.</div>}
          {projects.map((project) => (
            <div key={project.id} className="project-group">
              <button
                className={`project-row${project.id === activeProject?.id ? " active" : ""}`}
                onClick={() => setActiveProjectId(project.id)}
                title={project.folder}
              >
                <span className="project-icon">{project.source?.kind === "browser-local" ? "LOCAL" : "DIR"}</span>
                <span>{project.name}</span>
              </button>
              {project.chats.slice(0, 8).map((chat) => (
                <button
                  key={chat.id}
                  className={`chat-row${chat.id === activeChatId ? " active" : ""}`}
                  onClick={() => selectChat(project.id, chat)}
                >
                  <span>{chat.title}</span>
                  <small>{chat.status}</small>
                </button>
              ))}
            </div>
          ))}
        </div>
      </aside>}
      {showLeftResizer && (
        <div
          className="workspace-resizer"
          role="separator"
          aria-label="Resize sidebar"
          aria-orientation="vertical"
          tabIndex={0}
          onPointerDown={(event) => beginResize("left", event)}
          onKeyDown={(event) => resizeWithKeyboard("left", event)}
        />
      )}
      {visibleMain && <main className="workspace-main">
        <header className="workspace-topbar">
          <div>
            <strong>{activeProject ? activeProject.name : "No folder opened"}</strong>
            {activeProject && <span className="muted"> {activeProject.folder}</span>}
          </div>
          <div className="workspace-tools">
            <button className="ghost" onClick={() => setPaneOpen("main", false)}>Hide chat</button>
            <button className="ghost" onClick={() => toggleFullscreen("main")}>{fullscreenPane === "main" ? "Exit fullscreen" : "Fullscreen"}</button>
            <button className="ghost" onClick={() => setPaneOpen("summary", !sideOpen)}>{sideOpen ? "Hide summary" : "Show summary"}</button>
            <button className="ghost" onClick={props.onChangePassword}>Change password</button>
            <button className="ghost" onClick={props.onLogout}>Logout</button>
            <span className="muted">{props.user.username}</span>
          </div>
        </header>
        {props.error && <div className="workspace-error">{props.error}</div>}
        {syncStatus && <div className="workspace-sync">{syncStatus}</div>}
        <div ref={threadRef} className="chat-thread">
          {messages.length === 0 ? (
            <div className="chat-empty">
              <h1>What should DrChief do?</h1>
              <p>Open a folder, then ask a quick question or start a multi-agent workflow against that project.</p>
            </div>
          ) : (
            messages.map((message, index) => (
              <div key={`${message.time}-${index}`} className={`message-row ${message.role}`}>
                <div className="message-bubble">
                  <pre>{message.content}</pre>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="composer-wrap">
          <div className="composer">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onComposerKeyDown}
              placeholder={activeProject ? "Ask, edit, build, deploy..." : "Open a folder first..."}
              rows={3}
            />
            <div className="composer-bar">
              <button className="ghost" onClick={openFolder} disabled={folderBusy}>{folderBusy ? "Syncing..." : "Open local folder"}</button>
              <select value={mode} onChange={(e) => setMode(e.target.value as typeof mode)} title="Routing mode">
                <option value="auto">Auto route</option>
                <option value="quick">Quick answer</option>
                <option value="workflow">Workflow agents</option>
              </select>
              <select value={permissionMode} onChange={(e) => setPermissionMode(e.target.value as typeof permissionMode)} title="Permission mode">
                <option value="extended">Default permissions</option>
                <option value="bypass">Full access</option>
              </select>
              <button onClick={() => void submit()} disabled={busy || folderBusy || !input.trim()}>{busy ? "..." : "Send"}</button>
            </div>
          </div>
        </div>
      </main>}
      {showSummaryResizer && (
        <div
          className="workspace-resizer"
          role="separator"
          aria-label="Resize summary"
          aria-orientation="vertical"
          tabIndex={0}
          onPointerDown={(event) => beginResize("summary", event)}
          onKeyDown={(event) => resizeWithKeyboard("summary", event)}
        />
      )}
      {visibleSummary && (
        <aside className="workspace-sidepanel">
          <div className="workspace-panelbar">
            <strong>Summary</strong>
            <div className="workspace-panel-actions">
              <button className="ghost" onClick={() => setPaneOpen("summary", false)}>Hide</button>
              <button className="ghost" onClick={() => toggleFullscreen("summary")}>{fullscreenPane === "summary" ? "Exit fullscreen" : "Fullscreen"}</button>
            </div>
          </div>
          {runSessionId ? (
            <RunPanel sessionId={runSessionId} onDone={(sessionId) => void syncWorkflowBackToLocal(sessionId)} />
          ) : (
            <div className="side-empty">
              <h2>Summary</h2>
              <p>Workflow progress appears here when a request needs multiple agents.</p>
            </div>
          )}
        </aside>
      )}
    </div>
  );
}

const LOCAL_SYNC_EXCLUDES = new Set([".drchief2", "node_modules", ".git", ".DS_Store"]);
const LOCAL_SYNC_MAX_FILE_BYTES = 10 * 1024 * 1024;
const LOCAL_SYNC_BATCH_BYTES = 2 * 1024 * 1024;
const LOCAL_SYNC_BATCH_FILES = 24;

interface SnapshotFilePayload {
  path: string;
  contentBase64: string;
  size: number;
  lastModified?: number;
}

interface SnapshotBatch {
  reset?: boolean;
  directories: string[];
  files: SnapshotFilePayload[];
}

async function ensureLocalFolderPermission(handle: LocalDirectoryHandle): Promise<boolean> {
  const descriptor = { mode: "readwrite" as const };
  if (handle.queryPermission && await handle.queryPermission(descriptor) === "granted") return true;
  if (!handle.requestPermission) return true;
  return await handle.requestPermission(descriptor) === "granted";
}

async function uploadLocalFolderSnapshot(projectId: string, root: LocalDirectoryHandle, setStatus: (value: string | null) => void): Promise<void> {
  let batch: SnapshotBatch = { reset: true, directories: [], files: [] };
  let batchBytes = 0;
  let uploadedFiles = 0;
  let uploadedBytes = 0;
  const skipped: string[] = [];

  async function flush(): Promise<void> {
    if (!batch.reset && batch.directories.length === 0 && batch.files.length === 0) return;
    const r = await fetch(`/api/workspace/projects/${encodeURIComponent(projectId)}/snapshot`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(batch),
    });
    const j = (await r.json()) as { ok?: boolean; error?: string; filesWritten?: number; bytesWritten?: number };
    if (!j.ok) throw new Error(j.error ?? "Snapshot upload failed");
    uploadedFiles += j.filesWritten ?? 0;
    uploadedBytes += j.bytesWritten ?? 0;
    setStatus(`Synced ${uploadedFiles} files (${formatSyncBytes(uploadedBytes)}) from local folder...`);
    batch = { directories: [], files: [] };
    batchBytes = 0;
  }

  async function walk(dir: LocalDirectoryHandle, prefix: string): Promise<void> {
    for await (const entry of dir.values()) {
      if (LOCAL_SYNC_EXCLUDES.has(entry.name)) continue;
      const rel = prefix ? `${prefix}/${entry.name}` : entry.name;
      if (entry.kind === "directory") {
        batch.directories.push(rel);
        if (batch.directories.length > 120) await flush();
        await walk(entry, rel);
        continue;
      }
      const file = await entry.getFile();
      if (file.size > LOCAL_SYNC_MAX_FILE_BYTES) {
        skipped.push(`${rel} (${formatSyncBytes(file.size)})`);
        continue;
      }
      const contentBase64 = await fileToBase64(file);
      batch.files.push({ path: rel, contentBase64, size: file.size, lastModified: file.lastModified });
      batchBytes += file.size;
      if (batch.files.length >= LOCAL_SYNC_BATCH_FILES || batchBytes >= LOCAL_SYNC_BATCH_BYTES) await flush();
    }
  }

  setStatus(`Reading local folder "${root.name}"...`);
  await walk(root, "");
  await flush();
  const suffix = skipped.length ? ` Skipped ${skipped.length} large files.` : "";
  setStatus(`Local folder synced to agents: ${uploadedFiles} files, ${formatSyncBytes(uploadedBytes)}.${suffix}`);
}

async function syncSessionOutputToLocal(sessionId: string, root: LocalDirectoryHandle, setStatus: (value: string | null) => void): Promise<void> {
  const allowed = await ensureLocalFolderPermission(root);
  if (!allowed) throw new Error("local folder permission was not granted");
  setStatus("Reading workflow output for local sync...");
  const treeRes = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/tree`, { credentials: "include" });
  const tree = (await treeRes.json()) as { ok?: boolean; error?: string; entries?: Array<{ path: string; kind: "dir" | "file"; size?: number }> };
  if (!tree.ok || !tree.entries) throw new Error(tree.error ?? "Could not read workflow output");
  const directories = tree.entries.filter((e) => e.kind === "dir" && !pathHasLocalExcludedSegment(e.path));
  const files = tree.entries.filter((e) => e.kind === "file" && !pathHasLocalExcludedSegment(e.path));
  for (const dir of directories) await getLocalDirectory(root, dir.path.split("/"), true);

  let written = 0;
  for (const file of files) {
    const r = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/file?path=${encodeURIComponent(file.path)}`, { credentials: "include" });
    if (!r.ok) continue;
    const contentType = r.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
      const body = (await r.json()) as { ok?: boolean; kind?: string; text?: string };
      if (body.ok && body.kind === "text") {
        await writeLocalFile(root, file.path, body.text ?? "");
        written += 1;
      }
    } else {
      await writeLocalFile(root, file.path, await r.blob());
      written += 1;
    }
    if (written % 20 === 0) setStatus(`Applied ${written}/${files.length} files back to local folder...`);
  }

  if (tree.entries.length < 1990) {
    const remoteFilePaths = new Set(files.map((f) => f.path));
    const remoteDirPaths = new Set(directories.map((d) => d.path));
    await deleteLocalEntriesMissingFromRemote(root, remoteFilePaths, remoteDirPaths);
  }
  setStatus(`Workflow output synced back to local folder: ${written} files.`);
}

async function fileToBase64(file: File): Promise<string> {
  return arrayBufferToBase64(await file.arrayBuffer());
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

async function getLocalDirectory(root: LocalDirectoryHandle, parts: string[], create: boolean): Promise<LocalDirectoryHandle> {
  let current = root;
  for (const part of parts.filter(Boolean)) {
    current = await current.getDirectoryHandle(part, { create });
  }
  return current;
}

async function writeLocalFile(root: LocalDirectoryHandle, relPath: string, content: Blob | string): Promise<void> {
  const parts = relPath.split("/").filter(Boolean);
  const fileName = parts.pop();
  if (!fileName) return;
  const dir = await getLocalDirectory(root, parts, true);
  const fileHandle = await dir.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(content);
  await writable.close();
}

async function deleteLocalEntriesMissingFromRemote(root: LocalDirectoryHandle, remoteFilePaths: Set<string>, remoteDirPaths: Set<string>, prefix = ""): Promise<void> {
  for await (const entry of root.values()) {
    if (LOCAL_SYNC_EXCLUDES.has(entry.name)) continue;
    const rel = prefix ? `${prefix}/${entry.name}` : entry.name;
    if (entry.kind === "directory") {
      if (!remoteDirPaths.has(rel)) {
        await root.removeEntry(entry.name, { recursive: true });
      } else {
        await deleteLocalEntriesMissingFromRemote(entry, remoteFilePaths, remoteDirPaths, rel);
      }
      continue;
    }
    if (!remoteFilePaths.has(rel)) {
      await root.removeEntry(entry.name);
    }
  }
}

function pathHasLocalExcludedSegment(path: string): boolean {
  return path.split("/").some((part) => LOCAL_SYNC_EXCLUDES.has(part));
}

function formatSyncBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function RunPanel(props: { sessionId: string; onDone?: (sessionId: string) => void }) {
  const [events, setEvents] = useState<SessionStatusEvent[]>([]);
  const [dagNodes, setDagNodes] = useState<DagNode[]>([]);
  const [agentEvents, setAgentEvents] = useState<Record<string, AgentEventEntry[]>>({});
  const [done, setDone] = useState<{ status: string; summary: string } | null>(null);
  const [researchAudit, setResearchAudit] = useState<{ summary: string; sources: number; claims: number; unsupportedClaims: number; conflictedClaims: number } | null>(null);
  const [openAgent, setOpenAgent] = useState<string | null>(null);

  useEffect(() => {
    setEvents([]);
    setDagNodes([]);
    setAgentEvents({});
    setDone(null);
    setResearchAudit(null);
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${proto}//${window.location.host}/api/sessions/${props.sessionId}/stream`);
    ws.onmessage = (msg) => {
      try {
        const e = JSON.parse(msg.data) as SessionStatusEvent;
        setEvents((prev) => [...prev, e]);
        if (e.kind === "plan_ready") {
          const planEvent = e as unknown as { plan: { nodes: Array<{ id: string; executorType: string; dependsOn: string[] }> } };
          setDagNodes(planEvent.plan.nodes.map((n) => ({ id: n.id, status: "pending", executorType: n.executorType, dependsOn: n.dependsOn })));
        } else if (e.kind === "node_status") {
          const ne = e as unknown as { nodeId: string; status: string };
          setDagNodes((prev) => {
            const i = prev.findIndex((n) => n.id === ne.nodeId);
            if (i < 0) return [...prev, { id: ne.nodeId, status: ne.status }];
            const copy = prev.slice();
            copy[i] = { ...copy[i]!, status: ne.status };
            return copy;
          });
        } else if (e.kind === "agent_event") {
          const ae = e as unknown as { nodeId: string; level: string; message: string; time: string; data?: Record<string, unknown> };
          setAgentEvents((prev) => {
            const list = prev[ae.nodeId] ?? [];
            const next: AgentEventEntry = ae.data
              ? { level: ae.level, message: ae.message, time: ae.time, data: ae.data }
              : { level: ae.level, message: ae.message, time: ae.time };
            return { ...prev, [ae.nodeId]: [...list, next] };
          });
        } else if (e.kind === "session_done") {
          const d = e as unknown as { status: string; summary: string };
          setDone({ status: d.status, summary: d.summary });
          props.onDone?.(props.sessionId);
        } else if (e.kind === "research_audit") {
          const auditEvent = e as unknown as { audit: { summary: string; sources: number; claims: number; unsupportedClaims: number; conflictedClaims: number } };
          setResearchAudit(auditEvent.audit);
        }
      } catch {
        // ignore malformed websocket frames
      }
    };
    return () => ws.close();
  }, [props.sessionId]);

  const activeWork = useMemo(() => {
    const running = dagNodes.find((n) => n.status === "running");
    if (!running) return null;
    const list = agentEvents[running.id] ?? [];
    return { node: running, entry: list[list.length - 1] };
  }, [agentEvents, dagNodes]);

  return (
    <div className="run-panel">
      <div className="side-tabs">
        <strong>Summary</strong>
        <span className="muted">Review</span>
      </div>
      {done && <div className={`run-status ${done.status}`}>{done.status}: {done.summary}</div>}
      {researchAudit && (
        <div className="research-audit">
          <strong>Evidence</strong>
          <span>{researchAudit.sources} sources</span>
          <span>{researchAudit.claims} claims</span>
          <span>{researchAudit.unsupportedClaims} unsupported</span>
          <span>{researchAudit.conflictedClaims} conflicted</span>
        </div>
      )}
      <section>
        <h3>Progress</h3>
        <div className="progress-list">
          {dagNodes.length === 0 && <div className="muted">Waiting for workflow plan...</div>}
          {dagNodes.map((node) => (
            <button key={node.id} className={`progress-row ${node.status}`} onClick={() => setOpenAgent(node.id)}>
              <span className="status-dot" />
              <span>{node.id}</span>
              <small>{node.status}</small>
            </button>
          ))}
        </div>
      </section>
      {activeWork && (
        <section className="active-sidework">
          <h3>Active work</h3>
          <div>{activeWork.node.id}</div>
          <small className="muted">{activeWork.entry ? `${activeWork.entry.level}: ${activeWork.entry.message}` : "starting..."}</small>
        </section>
      )}
      <section>
        <h3>Workflow</h3>
        {dagNodes.length > 0 ? <DagFlow nodes={dagNodes} onNodeClick={setOpenAgent} height={260} /> : <div className="muted">No DAG yet.</div>}
      </section>
      {openAgent && (
        <section>
          <h3>{openAgent}</h3>
          <div className="side-agent-log">
            {(agentEvents[openAgent] ?? []).map((entry, index) => (
              <AgentEventRow key={index} entry={entry} colorFor={(level) => level === "error" ? "var(--danger)" : level === "warn" ? "var(--warn)" : "var(--text)"} />
            ))}
          </div>
        </section>
      )}
      <FilesBrowser sessionId={props.sessionId} pollOnLive={!done} />
      <section>
        <h3>Events</h3>
        <div className="event-stream side-events">
          {events.slice(-80).map((e, i) => (
            <div key={i} className="event-line"><span className="event-kind">{e.kind}</span> {JSON.stringify(e).slice(0, 160)}</div>
          ))}
        </div>
      </section>
    </div>
  );
}

async function fetchMe(): Promise<User | null> {
  try {
    const r = await fetch("/api/auth/me", { credentials: "include" });
    const j = (await r.json()) as { ok: boolean; authenticated?: boolean; user?: User };
    if (!j.ok || !j.authenticated || !j.user) return null;
    return j.user;
  } catch {
    return null;
  }
}

function Login(props: { onLogin: (u: User) => void; setError: (e: string | null) => void }) {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    props.setError(null);
    setBusy(true);
    try {
      const r = await fetch("/api/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const j = (await r.json()) as { ok: boolean; user?: User; error?: string };
      if (!j.ok || !j.user) {
        props.setError(j.error ?? "Login failed");
        return;
      }
      props.onLogin(j.user);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card login-card col" onSubmit={submit}>
      <h2>Sign in</h2>
      <div className="muted">Default admin: <code>admin / Aa@12345678901@</code> (change immediately)</div>
      <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" autoFocus />
      <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" />
      <button disabled={busy || !username || !password}>{busy ? "Signing in…" : "Sign in"}</button>
    </form>
  );
}

function ChangePassword(props: { onChanged: () => void; setError: (e: string | null) => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    props.setError(null);
    setBusy(true);
    try {
      const r = await fetch("/api/auth/change-password", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword: current, newPassword: next }),
      });
      const j = (await r.json()) as { ok: boolean; error?: string };
      if (!j.ok) {
        props.setError(j.error ?? "Change failed");
        return;
      }
      props.onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card login-card col" onSubmit={submit} autoComplete="off">
      <h2>Change password</h2>
      <div className="muted">Required after first login. Copy the current password exactly — browser autofill is disabled to prevent stale values from being submitted.</div>
      <input
        type="password"
        value={current}
        onChange={(e) => setCurrent(e.target.value)}
        placeholder="Current password"
        autoComplete="off"
        autoFocus
        name="current-password-no-autofill"
      />
      <input
        type="password"
        value={next}
        onChange={(e) => setNext(e.target.value)}
        placeholder="New password (≥ 12 chars)"
        autoComplete="new-password"
        name="new-password-no-autofill"
      />
      <button disabled={busy || !current || next.length < 12}>{busy ? "Saving…" : "Save"}</button>
    </form>
  );
}

interface AccountSnapshot {
  providerId: string;
  accountLabel: string;
  inFlight: number;
  maxConcurrent: number;
  totalInputTokens?: number;
  totalOutputTokens?: number;
  totalReasoningTokens?: number;
  totalCalls?: number;
  estimatedCostUsd?: number;
}

interface CostByAgent {
  agentType: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  costUsd: number;
}

interface SessionSummary {
  sessionId: string;
  folder: string;
  request: string;
  permissionMode: string;
  startedAt: string;
  finished: boolean;
  finalStatus: string;
  finishedAt?: string;
  eventsCount: number;
}

type PermissionMode = "safe" | "extended" | "bypass";

/**
 * Always-visible left sidebar that lists every session in the orchestrator's
 * current memory (running + finished within this boot). Click any row to
 * jump to that session's Stream view; the WebSocket replays the full
 * event log so reattach works regardless of when the session started.
 *
 * Polls /api/sessions every 4 seconds. Persists open/closed state in
 * localStorage so the user's pref survives reload.
 */
function SessionSidebar(props: {
  activeSessionId: string | null;
  onOpenSession: (sessionId: string, permissionMode: string) => void;
  onNewSession: () => void;
  onCollapse: () => void;
}) {
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [aborting, setAborting] = useState<Set<string>>(new Set());

  const refresh = () => {
    fetch("/api/sessions", { credentials: "include" })
      .then((r) => r.json())
      .then((j) => {
        if (j?.ok && Array.isArray(j.sessions)) setSessions(j.sessions);
      })
      .catch(() => {});
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 4_000);
    return () => { clearInterval(t); };
  }, []);

  const abortOne = async (sessionId: string) => {
    setAborting((prev) => new Set(prev).add(sessionId));
    try {
      await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/abort`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
    } finally {
      setAborting((prev) => {
        const n = new Set(prev);
        n.delete(sessionId);
        return n;
      });
      refresh();
    }
  };

  const abortAll = async () => {
    const running = sessions.filter((s) => !s.finished);
    if (running.length === 0) return;
    if (!confirm(`Abort ${running.length} running session${running.length > 1 ? "s" : ""}? Children will be killed and the runs marked failed.`)) return;
    setAborting(new Set(running.map((s) => s.sessionId)));
    try {
      await fetch("/api/sessions/abort-all", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
    } finally {
      setAborting(new Set());
      refresh();
    }
  };

  const running = sessions.filter((s) => !s.finished);
  const finished = sessions.filter((s) => s.finished);

  return (
    <aside className="sidebar">
      <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <strong>Sessions</strong>
        <button className="ghost" onClick={props.onCollapse} title="Collapse sidebar">←</button>
      </div>
      <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 6 }}>
        <button onClick={props.onNewSession} style={{ width: "100%" }}>+ New session</button>
        {running.length > 0 && (
          <button
            onClick={abortAll}
            style={{
              width: "100%",
              background: "var(--danger)",
              color: "#fff",
              fontSize: 12,
              padding: "6px 10px",
            }}
            title={`Abort ${running.length} running session(s)`}
          >
            ◼ Abort all ({running.length})
          </button>
        )}
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "8px 10px" }}>
        {sessions.length === 0 && (
          <div className="muted" style={{ fontSize: 12, padding: "12px 4px" }}>
            No sessions yet — click "+ New session" to start.
          </div>
        )}
        {running.length > 0 && (
          <>
            <div className="muted" style={{ fontSize: 11, marginTop: 4, marginBottom: 4, letterSpacing: 0.5 }}>RUNNING</div>
            {running.map((s) => (
              <SidebarSessionRow
                key={s.sessionId}
                session={s}
                active={s.sessionId === props.activeSessionId}
                aborting={aborting.has(s.sessionId)}
                onOpen={() => props.onOpenSession(s.sessionId, s.permissionMode)}
                onAbort={() => abortOne(s.sessionId)}
              />
            ))}
          </>
        )}
        {finished.length > 0 && (
          <>
            <div className="muted" style={{ fontSize: 11, marginTop: 12, marginBottom: 4, letterSpacing: 0.5 }}>FINISHED</div>
            {finished.map((s) => (
              <SidebarSessionRow
                key={s.sessionId}
                session={s}
                active={s.sessionId === props.activeSessionId}
                aborting={false}
                onOpen={() => props.onOpenSession(s.sessionId, s.permissionMode)}
              />
            ))}
          </>
        )}
      </div>
    </aside>
  );
}

function SidebarSessionRow(props: {
  session: SessionSummary;
  active: boolean;
  aborting: boolean;
  onOpen: () => void;
  onAbort?: () => void;
}) {
  const s = props.session;
  const colour =
    s.finalStatus === "running" ? "var(--accent)"
    : s.finalStatus === "done" ? "var(--ok)"
    : s.finalStatus === "warned" ? "var(--warn)"
    : "var(--danger)";
  const folderName = s.folder.split(/[\\/]/).pop() || s.sessionId;
  const isRunning = !s.finished;
  return (
    <div
      onClick={props.onOpen}
      style={{
        cursor: "pointer",
        padding: "8px 10px",
        margin: "3px 0",
        borderRadius: 6,
        border: `1px solid ${props.active ? colour : "transparent"}`,
        background: props.active ? "var(--surface-2)" : "transparent",
        position: "relative",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: colour, flexShrink: 0 }} />
        <span style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
          {folderName}
        </span>
        {isRunning && props.onAbort && (
          <button
            onClick={(e) => { e.stopPropagation(); props.onAbort?.(); }}
            disabled={props.aborting}
            title="Abort this session"
            style={{
              background: "transparent",
              color: "var(--danger)",
              border: `1px solid var(--danger)`,
              padding: "1px 6px",
              fontSize: 10,
              fontWeight: 600,
              borderRadius: 4,
              cursor: props.aborting ? "wait" : "pointer",
              flexShrink: 0,
            }}
          >
            {props.aborting ? "…" : "Abort"}
          </button>
        )}
      </div>
      <div className="muted" style={{ fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginLeft: 13 }}>
        {s.request}
      </div>
      <div className="muted" style={{ fontSize: 10, marginLeft: 13 }}>
        {relTime(s.startedAt)} · {s.eventsCount}e · {s.finalStatus}
      </div>
    </div>
  );
}

function SessionRow(props: { session: SessionSummary; onOpen: () => void }) {
  const s = props.session;
  const colour =
    s.finalStatus === "running" ? "var(--accent)"
    : s.finalStatus === "done" ? "var(--ok)"
    : s.finalStatus === "warned" ? "var(--warn)"
    : "var(--danger)";
  const sinceStart = relTime(s.startedAt);
  return (
    <div
      onClick={props.onOpen}
      style={{
        cursor: "pointer",
        padding: "10px 12px",
        margin: "6px 0",
        borderRadius: 6,
        border: `1px solid ${colour}`,
        background: "var(--surface-2)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 600, color: colour }}>
          [{s.finalStatus}] {s.folder.split(/[\\/]/).pop() || s.sessionId}
        </div>
        <div className="muted" style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {s.request}
        </div>
        <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
          {s.sessionId} · {sinceStart} · {s.eventsCount} events · perm={s.permissionMode}
        </div>
      </div>
      <span style={{ color: "var(--accent)", fontSize: 14 }}>↗</span>
    </div>
  );
}

function relTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return iso;
  const diff = Math.max(0, Date.now() - t);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

interface TaskTemplate {
  id: string;
  title: string;
  tagline: string;
  glyph: string;
  folderHint: string;
  request: string;
  permissionMode: PermissionMode;
  costBand: "low" | "med" | "high";
  category: "build" | "audit" | "refactor" | "research" | "ops";
}

function Sessions(props: { onStarted: (id: string, permissionMode: string) => void; setError: (e: string | null) => void }) {
  const [folder, setFolder] = useState("");
  const [request, setRequest] = useState("");
  const [permissionMode, setPermissionMode] = useState<PermissionMode>(
    (localStorage.getItem("drchief.permissionMode") as PermissionMode) || "extended",
  );
  const [busy, setBusy] = useState(false);
  const [accounts, setAccounts] = useState<AccountSnapshot[]>([]);
  const [costByAgent, setCostByAgent] = useState<CostByAgent[]>([]);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [activeCategory, setActiveCategory] = useState<TaskTemplate["category"] | "all">("all");
  // Auto-commit options. Default off — opt-in via the checkbox.
  const [autoCommitEnabled, setAutoCommitEnabled] = useState(false);
  const [autoCommitInit, setAutoCommitInit] = useState(true);
  const [autoCommitPush, setAutoCommitPush] = useState(false);
  const [autoCommitBranch, setAutoCommitBranch] = useState("");
  const [vpsSyncEnabled, setVpsSyncEnabled] = useState(false);
  const [vpsSyncHost, setVpsSyncHost] = useState("");
  const [vpsSyncPath, setVpsSyncPath] = useState("");
  // Webhook notification options.
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  // Voice input (Web Speech API). Only enabled when the browser supports it.
  const [listening, setListening] = useState(false);
  const recogRef = useRef<unknown>(null);

  useEffect(() => {
    localStorage.setItem("drchief.permissionMode", permissionMode);
  }, [permissionMode]);

  // Fetch templates once on mount. Static content — no need to refresh.
  useEffect(() => {
    fetch("/api/templates", { credentials: "include" })
      .then((r) => r.json())
      .then((j) => {
        if (j?.ok && Array.isArray(j.templates)) setTemplates(j.templates);
      })
      .catch(() => {});
  }, []);

  function applyTemplate(t: TaskTemplate) {
    setFolder(t.folderHint);
    setRequest(t.request);
    setPermissionMode(t.permissionMode);
  }

  // Web Speech API mic. Browser-side only — when SpeechRecognition isn't
  // available (Firefox / Safari old) the button shows a toast; on Chrome
  // / Edge it appends transcript to the request textarea live.
  const speechSupported = typeof window !== "undefined"
    && (("SpeechRecognition" in window) || ("webkitSpeechRecognition" in window));

  function toggleMic() {
    if (!speechSupported) {
      props.setError("Voice input not supported in this browser. Try Chrome or Edge.");
      return;
    }
    if (listening && recogRef.current) {
      try { (recogRef.current as { stop?: () => void }).stop?.(); } catch { /* ignored */ }
      return;
    }
    const w = window as unknown as { SpeechRecognition?: typeof globalThis.SpeechRecognition; webkitSpeechRecognition?: typeof globalThis.SpeechRecognition };
    const Recog = (w.SpeechRecognition ?? w.webkitSpeechRecognition) as new () => SpeechRecognition;
    const r = new Recog();
    r.lang = "vi-VN";
    r.continuous = false;
    r.interimResults = true;
    r.onresult = (ev: SpeechRecognitionEvent) => {
      let str = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const result = ev.results[i];
        const alt = result?.[0];
        if (alt) str += alt.transcript;
      }
      setRequest((prev) => (prev ? prev.replace(/\s+$/, "") + " " : "") + str);
    };
    r.onend = () => { setListening(false); recogRef.current = null; };
    r.onerror = (ev: SpeechRecognitionErrorEvent) => { setListening(false); props.setError(`Mic: ${ev.error}`); };
    recogRef.current = r;
    setListening(true);
    try { r.start(); } catch (e) { setListening(false); props.setError((e as Error).message); }
  }

  const visibleTemplates = activeCategory === "all"
    ? templates
    : templates.filter((t) => t.category === activeCategory);
  const categoryLabels: Record<TaskTemplate["category"] | "all", string> = {
    all: "All",
    build: "🛠 Build",
    audit: "🔎 Audit",
    refactor: "♻️ Refactor",
    research: "📚 Research",
    ops: "⚙️ Ops",
  };

  useEffect(() => {
    let alive = true;
    const refresh = () => {
      fetch("/api/broker", { credentials: "include" })
        .then((r) => r.json())
        .then((j) => {
          if (!alive) return;
          if (j?.ok) {
            if (Array.isArray(j.accounts)) setAccounts(j.accounts);
            if (Array.isArray(j.costByAgentType)) setCostByAgent(j.costByAgentType);
          }
        })
        .catch(() => {});
      fetch("/api/sessions", { credentials: "include" })
        .then((r) => r.json())
        .then((j) => {
          if (!alive) return;
          if (j?.ok && Array.isArray(j.sessions)) setSessions(j.sessions);
        })
        .catch(() => {});
    };
    refresh();
    const t = setInterval(refresh, 4_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const totalCost = accounts.reduce((sum, a) => sum + (a.estimatedCostUsd ?? 0), 0);
  const totalCalls = accounts.reduce((sum, a) => sum + (a.totalCalls ?? 0), 0);

  async function startSession(e: React.FormEvent) {
    e.preventDefault();
    props.setError(null);
    setBusy(true);
    try {
      // Build the request payload. autoCommit / notify are omitted entirely
      // when disabled so the server's default paths run verbatim.
      const payload: Record<string, unknown> = { folder, request, permissionMode };
      if (autoCommitEnabled) {
        payload.autoCommit = {
          initIfMissing: autoCommitInit,
          push: autoCommitPush,
          ...(autoCommitBranch ? { branch: autoCommitBranch } : {}),
        };
      }
      if (vpsSyncEnabled) {
        payload.vpsSync = {
          enabled: true,
          ...(vpsSyncHost.trim() ? { host: vpsSyncHost.trim() } : {}),
          ...(vpsSyncPath.trim() ? { remotePath: vpsSyncPath.trim() } : {}),
        };
      }
      if (webhookEnabled && webhookUrl.trim()) {
        payload.notify = { webhookUrl: webhookUrl.trim() };
      }
      const r = await fetch("/api/sessions", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const j = (await r.json()) as { ok: boolean; sessionId?: string; permissionMode?: string; error?: string };
      if (!j.ok || !j.sessionId) {
        props.setError(j.error ?? "Failed to start session");
        return;
      }
      props.onStarted(j.sessionId, j.permissionMode ?? permissionMode);
    } finally {
      setBusy(false);
    }
  }

  const running = sessions.filter((s) => !s.finished);
  const finished = sessions.filter((s) => s.finished);

  return (
    <div className="shell">
      {sessions.length === 0 && (
        <div className="card hero-card">
          <h1 style={{ fontSize: 28, margin: "0 0 8px", lineHeight: 1.2 }}>What would you like DrChief to build?</h1>
          <div className="muted" style={{ fontSize: 14, marginBottom: 12 }}>
            Pick a template below or describe your own task in the form. Sessions run autonomously — Workflow plans the DAG, Manager dispatches Executors, Reviewer gates output, and the system auto-recovers on stalls. You can pause / abort any session from the sidebar.
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            Today's deployment: hybrid <strong>Moonshot Kimi K2.6 + DeepSeek V4</strong> via <code>/api/broker</code>. Cost varies by tier.
          </div>
        </div>
      )}
      <div className="card">
        <h2>Sessions ({sessions.length})</h2>
        {sessions.length === 0 ? (
          <div className="muted">No sessions yet — pick a template below or write your own task.</div>
        ) : (
          <div>
            {running.length > 0 && (
              <>
                <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>RUNNING ({running.length})</div>
                {running.map((s) => (
                  <SessionRow key={s.sessionId} session={s} onOpen={() => props.onStarted(s.sessionId, s.permissionMode)} />
                ))}
              </>
            )}
            {finished.length > 0 && (
              <>
                <div className="muted" style={{ fontSize: 12, margin: "12px 0 4px" }}>FINISHED ({finished.length})</div>
                {finished.map((s) => (
                  <SessionRow key={s.sessionId} session={s} onOpen={() => props.onStarted(s.sessionId, s.permissionMode)} />
                ))}
              </>
            )}
          </div>
        )}
      </div>
      <div className="card">
        <h2>Provider broker</h2>
        {accounts.length === 0 ? (
          <div className="muted">No provider accounts registered.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--muted)" }}>
                <th>Account</th>
                <th>Load</th>
                <th>Calls</th>
                <th>In tokens</th>
                <th>Out tokens</th>
                <th>USD</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={`${a.providerId}/${a.accountLabel}`}>
                  <td><code>{a.providerId}/{a.accountLabel}</code></td>
                  <td>{a.inFlight}/{a.maxConcurrent}</td>
                  <td>{a.totalCalls ?? 0}</td>
                  <td>{(a.totalInputTokens ?? 0).toLocaleString()}</td>
                  <td>{(a.totalOutputTokens ?? 0).toLocaleString()}</td>
                  <td>{(a.estimatedCostUsd ?? 0).toFixed(4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="muted" style={{ marginTop: 12 }}>
          Total: {totalCalls.toLocaleString()} calls, ${totalCost.toFixed(4)} estimated.
          OAuth subscription accounts are billed at $0 marginal but tokens are tracked.
        </div>
      </div>
      {costByAgent.length > 0 && (
        <div className="card">
          <h2>Cost by agent type</h2>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--muted)" }}>
                <th>Agent</th>
                <th>Calls</th>
                <th>In</th>
                <th>Out</th>
                <th>Reasoning</th>
                <th>USD</th>
              </tr>
            </thead>
            <tbody>
              {costByAgent.map((c) => (
                <tr key={c.agentType}>
                  <td><code>{c.agentType}</code></td>
                  <td>{c.calls}</td>
                  <td>{c.inputTokens.toLocaleString()}</td>
                  <td>{c.outputTokens.toLocaleString()}</td>
                  <td>{c.reasoningTokens.toLocaleString()}</td>
                  <td>{c.costUsd.toFixed(4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <form className="card col" onSubmit={startSession}>
        <h2>New session</h2>
        {templates.length > 0 && (
          <div className="template-strip">
            <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
              Quickstart templates — click to load. Each pre-fills the project name, request, and permission mode.
            </div>
            <div className="template-tabs" role="tablist">
              {(["all", "build", "audit", "refactor", "research", "ops"] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  role="tab"
                  aria-selected={activeCategory === c}
                  className={`template-tab ${activeCategory === c ? "is-active" : ""}`}
                  onClick={() => setActiveCategory(c)}
                >
                  {categoryLabels[c]}
                </button>
              ))}
            </div>
            <div className="template-grid">
              {visibleTemplates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className="template-card"
                  onClick={() => applyTemplate(t)}
                  title={t.tagline}
                >
                  <div className="template-card-head">
                    <span className="template-glyph" aria-hidden="true">{t.glyph}</span>
                    <strong>{t.title}</strong>
                    <span className={`template-cost-band band-${t.costBand}`} title={`Estimated cost band: ${t.costBand}`}>{t.costBand}</span>
                  </div>
                  <div className="template-tagline">{t.tagline}</div>
                </button>
              ))}
            </div>
          </div>
        )}
        <label>Project name</label>
        <input
          value={folder}
          onChange={(e) => setFolder(e.target.value)}
          placeholder="my-todo-app"
        />
        <div className="muted" style={{ fontSize: 12 }}>
          A short slug (lowercase letters, digits, <code>-</code> or <code>_</code>). The
          server creates this session under <code>&lt;repo&gt;/sessions/&lt;slug&gt;</code>,
          so files can be committed, pushed to GitHub, and pulled by the VPS.
          Absolute paths are accepted only when they stay under the repo root.
        </div>
        <label>Permission mode</label>
        <select
          value={permissionMode}
          onChange={(e) => setPermissionMode(e.target.value as PermissionMode)}
          style={{
            background: "var(--surface-2)",
            color: "var(--text)",
            border: `1px solid ${permissionMode === "bypass" ? "var(--warn)" : "var(--border)"}`,
            padding: "8px 12px",
            borderRadius: 6,
            font: "inherit",
            width: "100%",
          }}
        >
          <option value="extended">Extended - full workflow authority inside this session folder (default)</option>
          <option value="safe">Safe - per-agent allowlist + path containment + shell binary allowlist</option>
          <option value="bypass">Bypass permissions — TURBO full access (no allowlists, no path containment)</option>
        </select>
        {permissionMode === "bypass" && (
          <div className="warn" style={{ fontSize: 12, lineHeight: 1.4 }}>
            ⚠ Bypass mode: the agent can write anywhere the <code>drchief</code> OS user
            can write, run any binary present on the host, and read the full filesystem.
            Linux DAC + systemd hardening (ProtectSystem=full, ProtectHome=true,
            unprivileged uid) remain the hard perimeter — but the soft guards are off.
            Use only for sessions you fully trust.
          </div>
        )}
        <label>
          Request
          {speechSupported && (
            <button
              type="button"
              className={`mic-button ${listening ? "is-listening" : ""}`}
              onClick={toggleMic}
              title={listening ? "Stop listening" : "Dictate request (Vietnamese / English)"}
              aria-pressed={listening}
              style={{ marginLeft: 8, minHeight: 0, padding: "4px 10px", fontSize: 13 }}
            >
              {listening ? "🔴 Stop mic" : "🎤 Dictate"}
            </button>
          )}
        </label>
        <textarea
          value={request}
          onChange={(e) => setRequest(e.target.value)}
          rows={5}
          placeholder="Describe what you want built. The Workflow agent will plan the DAG. Examples: 'Build a TODO app with React + Vite', 'Audit security of this codebase + auto-fix critical findings', 'Add Vitest coverage to 70%+'."
        />
        <details className="autocommit-block" open={autoCommitEnabled}>
          <summary>
            <input
              type="checkbox"
              checked={autoCommitEnabled}
              onChange={(e) => setAutoCommitEnabled(e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              style={{ marginRight: 8 }}
            />
            Auto-commit on success
            <span className="muted" style={{ fontSize: 11, marginLeft: 8 }}>
              When the session reaches done/warned, commit only this session folder.
            </span>
          </summary>
          {autoCommitEnabled && (
            <div className="autocommit-options">
              <label className="row-flex">
                <input
                  type="checkbox"
                  checked={autoCommitInit}
                  onChange={(e) => setAutoCommitInit(e.target.checked)}
                />
                <span><code>git init</code> if folder is not yet a repo</span>
              </label>
              <label className="row-flex">
                <input
                  type="checkbox"
                  checked={autoCommitPush}
                  onChange={(e) => setAutoCommitPush(e.target.checked)}
                />
                <span>Also <code>git push origin</code> after commit (GitHub sync option)</span>
              </label>
              {autoCommitPush && (
                <label className="row-flex">
                  <span style={{ width: 80 }}>Branch:</span>
                  <input
                    type="text"
                    value={autoCommitBranch}
                    onChange={(e) => setAutoCommitBranch(e.target.value)}
                    placeholder="(current branch)"
                    style={{ flex: 1 }}
                  />
                </label>
              )}
              <div className="muted" style={{ fontSize: 11 }}>
                Commit message defaults to <code>&lt;sessionId&gt;: &lt;your request, truncated&gt;</code>. Failures are non-fatal — they're logged in the session_done event but don't flip the session status.
              </div>
            </div>
          )}
        </details>
        <details className="autocommit-block" open={vpsSyncEnabled}>
          <summary>
            <input
              type="checkbox"
              checked={vpsSyncEnabled}
              onChange={(e) => setVpsSyncEnabled(e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              style={{ marginRight: 8 }}
            />
            Sync VPS after success
            <span className="muted" style={{ fontSize: 11, marginLeft: 8 }}>
              Runs the operator-configured VPS sync command after done/warned.
            </span>
          </summary>
          {vpsSyncEnabled && (
            <div className="autocommit-options">
              <label className="row-flex">
                <span style={{ width: 90 }}>SSH host:</span>
                <input
                  type="text"
                  value={vpsSyncHost}
                  onChange={(e) => setVpsSyncHost(e.target.value)}
                  placeholder="(DRCHIEF_VPS_SYNC_HOST, e.g. drchief-vps)"
                  style={{ flex: 1 }}
                />
              </label>
              <label className="row-flex">
                <span style={{ width: 90 }}>Remote path:</span>
                <input
                  type="text"
                  value={vpsSyncPath}
                  onChange={(e) => setVpsSyncPath(e.target.value)}
                  placeholder="(DRCHIEF_VPS_SYNC_PATH, e.g. /opt/drchief2)"
                  style={{ flex: 1 }}
                />
              </label>
              <div className="muted" style={{ fontSize: 11 }}>
                If <code>DRCHIEF_VPS_SYNC_COMMAND</code> is set, that command wins. Otherwise DrChief runs SSH to pull, build, and restart <code>drchief.service</code>. For git-based VPS sync, enable auto-commit and push first.
              </div>
            </div>
          )}
        </details>
        <details className="autocommit-block" open={webhookEnabled}>
          <summary>
            <input
              type="checkbox"
              checked={webhookEnabled}
              onChange={(e) => setWebhookEnabled(e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              style={{ marginRight: 8 }}
            />
            Webhook on session_done
            <span className="muted" style={{ fontSize: 11, marginLeft: 8 }}>
              POST a JSON payload to a URL when this session ends. Slack / Discord / generic.
            </span>
          </summary>
          {webhookEnabled && (
            <div className="autocommit-options">
              <label className="row-flex">
                <span style={{ width: 90 }}>Webhook URL:</span>
                <input
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://hooks.slack.com/services/T.../B.../..."
                  style={{ flex: 1 }}
                />
              </label>
              <div className="muted" style={{ fontSize: 11 }}>
                Fires on every terminal status (done / warned / failed / error). Loopback / private hosts are blocked unless the operator sets <code>DRCHIEF_WEBHOOK_ALLOW_INTERNAL=true</code>. Hard timeout 10 s.
              </div>
            </div>
          )}
        </details>
        <button disabled={busy || !folder || request.length < 8}>{busy ? "Starting…" : "Start session"}</button>
      </form>
      <div className="card">
        <h2>Repo-local session folders</h2>
        <div className="muted">
          Web sessions are created under the local repo's <code>sessions/</code> directory.
          Use auto-commit + push for GitHub sync, then VPS sync to pull and restart production.
          CLI mode can run the same workflow against a repo checkout on your own machine:
        </div>
        <pre className="event-stream" style={{ marginTop: 8 }}>
{`git clone -b v2/main https://github.com/vh0202/DrChief.git
cd DrChief
npm install && npm run build
echo MOONSHOT_API_KEY=... > .env
echo DEEPSEEK_API_KEY=... >> .env
node dist/cli/run.js "C:\\path\\to\\local-folder" "your request"`}
        </pre>
        <div className="muted" style={{ marginTop: 8 }}>
          The CLI uses the same agent topology and writes directly to whatever folder you
          pass.
        </div>
      </div>
    </div>
  );
}

interface DagNode {
  id: string;
  status: string;
  executorType?: string;
  dependsOn?: string[];
}

interface AgentEventEntry {
  level: string;
  message: string;
  time: string;
  data?: Record<string, unknown>;
}

/**
 * Render one agent event with a rendering tailored to its kind:
 *   - turn          → "what the agent is thinking" with reply preview
 *   - tool_request  → "→ writing src/foo.ts (1234 chars)" + preview
 *   - tool_result   → "✓ wrote N bytes" / "✗ exit 1: ..." with stdout
 *   - complete      → green DONE banner with summary
 *   - lifecycle/warn/error → simple level + message line
 */
function AgentEventRow(props: { entry: AgentEventEntry; colorFor: (l: string) => string }) {
  const e = props.entry;
  const time = e.time.slice(11, 19);
  const colour = props.colorFor(e.level);
  const baseStyle: React.CSSProperties = { padding: "6px 0", borderBottom: "1px dashed var(--border)" };
  const headerStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", gap: 8 };

  if (e.level === "turn" && e.data && typeof e.data.replyPreview === "string") {
    const d = e.data as { replyPreview: string; model?: string; inputTokens?: number; outputTokens?: number; step?: number };
    return (
      <div style={baseStyle}>
        <div style={headerStyle}>
          <span style={{ color: colour, fontWeight: 600 }}>turn{d.step ? ` #${d.step}` : ""}</span>
          <span className="muted">
            {d.model ? `${d.model} · ` : ""}{d.inputTokens ?? 0}→{d.outputTokens ?? 0}t · {time}
          </span>
        </div>
        <pre style={{ margin: "4px 0 0", whiteSpace: "pre-wrap", wordBreak: "break-word", color: "var(--text)" }}>
          {d.replyPreview}
        </pre>
      </div>
    );
  }

  if (e.level === "llm_wait" || e.level === "llm_start" || e.level === "llm_done") {
    const d = (e.data ?? {}) as { provider?: string; model?: string; tier?: string; elapsedMs?: number; timeoutMs?: number; attempt?: number };
    const elapsed = typeof d.elapsedMs === "number" ? `${Math.round(d.elapsedMs / 1000)}s` : null;
    const timeout = typeof d.timeoutMs === "number" ? `${Math.round(d.timeoutMs / 1000)}s max` : null;
    const model = d.provider && d.model ? `${d.provider}/${d.model}` : d.model;
    return (
      <div style={baseStyle}>
        <div style={headerStyle}>
          <span style={{ color: colour, fontWeight: 600 }}>{e.level.replace("_", " ")}</span>
          <span className="muted">{time}</span>
        </div>
        <div style={{ marginTop: 2 }}>
          {e.message}
          {model ? <span className="muted"> - {model}</span> : null}
        </div>
        {(elapsed || timeout || d.tier || d.attempt) && (
          <div className="muted" style={{ marginTop: 2, fontSize: 11 }}>
            {[d.tier, d.attempt ? `attempt ${d.attempt}` : null, elapsed, timeout].filter(Boolean).join(" · ")}
          </div>
        )}
      </div>
    );
  }

  if (e.level === "tool_request") {
    const d = (e.data ?? {}) as { argsPreview?: { path?: string; contentLength?: number; contentPreview?: string } };
    const preview = d.argsPreview;
    return (
      <div style={baseStyle}>
        <div style={headerStyle}>
          <span style={{ color: colour, fontWeight: 600 }}>→ {e.message}</span>
          <span className="muted">{time}</span>
        </div>
        {preview?.contentPreview && (
          <pre style={{ margin: "4px 0 0", whiteSpace: "pre-wrap", wordBreak: "break-word", color: "var(--muted)", maxHeight: 160, overflow: "auto" }}>
            {preview.contentPreview}
            {typeof preview.contentLength === "number" && preview.contentLength > preview.contentPreview.length
              ? `\n… (${(preview.contentLength - preview.contentPreview.length).toLocaleString()} more chars)`
              : ""}
          </pre>
        )}
      </div>
    );
  }

  if (e.level === "tool_result") {
    const d = (e.data ?? {}) as {
      tool?: string;
      ok?: boolean;
      resultPreview?: {
        path?: string;
        bytes?: number;
        contentPreview?: string;
        count?: number;
        sample?: Array<{ name?: string; kind?: string }>;
        exitCode?: number | null;
        stdoutPreview?: string;
        stderrPreview?: string;
        error?: string;
      };
    };
    const r = d.resultPreview ?? {};
    let summary: React.ReactNode = e.message;
    if (d.ok) {
      if (d.tool === "fs.write_file") summary = `✓ wrote ${r.path ?? ""} (${r.bytes ?? 0} bytes)`;
      else if (d.tool === "fs.read_file") summary = `✓ read ${r.bytes ?? 0} bytes`;
      else if (d.tool === "fs.list_dir") summary = `✓ ${r.count ?? 0} entries`;
      else if (d.tool === "shell.exec") summary = `${r.exitCode === 0 ? "✓" : "✗"} exit ${r.exitCode ?? "?"}`;
    } else {
      summary = `✗ ${d.tool ?? ""}: ${r.error ?? "failed"}`;
    }
    return (
      <div style={baseStyle}>
        <div style={headerStyle}>
          <span style={{ color: d.ok ? colour : "var(--danger)", fontWeight: 600 }}>{summary}</span>
          <span className="muted">{time}</span>
        </div>
        {(r.contentPreview || r.stdoutPreview || r.stderrPreview) && (
          <pre style={{ margin: "4px 0 0", whiteSpace: "pre-wrap", wordBreak: "break-word", color: "var(--muted)", maxHeight: 200, overflow: "auto" }}>
            {r.contentPreview ?? r.stdoutPreview ?? ""}
            {r.stderrPreview ? `\n— stderr —\n${r.stderrPreview}` : ""}
          </pre>
        )}
        {r.sample && (
          <div style={{ margin: "4px 0 0", color: "var(--muted)" }}>
            {r.sample.map((s) => `${s.kind === "dir" ? "📁" : "📄"} ${s.name}`).join("  ")}
          </div>
        )}
      </div>
    );
  }

  if (e.level === "complete") {
    const d = (e.data ?? {}) as { summary?: string; totalSteps?: number; totalToolCalls?: number };
    return (
      <div style={{ ...baseStyle, background: "rgba(109,255,174,0.06)", marginTop: 4, padding: "8px", borderRadius: 4 }}>
        <div style={headerStyle}>
          <span style={{ color: colour, fontWeight: 600 }}>✓ complete</span>
          <span className="muted">{time}</span>
        </div>
        <div style={{ marginTop: 4 }}>{d.summary || e.message}</div>
        {(d.totalSteps || d.totalToolCalls) && (
          <div className="muted" style={{ marginTop: 2, fontSize: 11 }}>
            {d.totalSteps ?? "?"} turns · {d.totalToolCalls ?? "?"} tool calls
          </div>
        )}
      </div>
    );
  }

  // Default: simple level + message + raw data dump
  return (
    <div style={baseStyle}>
      <div style={headerStyle}>
        <span style={{ color: colour, fontWeight: 600 }}>{e.level}</span>
        <span className="muted">{time}</span>
      </div>
      <div style={{ marginTop: 2 }}>{e.message}</div>
      {e.data && Object.keys(e.data).length > 0 && (
        <pre style={{ margin: "4px 0 0", color: "var(--muted)", whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 120, overflow: "auto" }}>
          {JSON.stringify(e.data, null, 2).slice(0, 800)}
        </pre>
      )}
    </div>
  );
}

function StatusBar(props: { permissionMode: string; broker: string }) {
  const danger = props.permissionMode === "bypass";
  return (
    <div
      style={{
        position: "sticky",
        bottom: 0,
        marginTop: 16,
        padding: "8px 16px",
        background: "var(--surface)",
        borderTop: `1px solid ${danger ? "var(--warn)" : "var(--border)"}`,
        display: "flex",
        justifyContent: "space-between",
        fontSize: 13,
        color: danger ? "var(--warn)" : "var(--muted)",
      }}
    >
      <span>
        Permission: <strong style={{ color: danger ? "var(--warn)" : "var(--accent)" }}>{props.permissionMode}</strong>
        {danger && " — TURBO full access"}
      </span>
      <span>Brokers: {props.broker}</span>
    </div>
  );
}

function Stream(props: { sessionId: string; onBack: () => void; permissionMode: string; onSessionStarted?: (id: string, mode: string) => void }) {
  const [events, setEvents] = useState<SessionStatusEvent[]>([]);
  const [dagNodes, setDagNodes] = useState<DagNode[]>([]);
  const [drift, setDrift] = useState<{ score: number; concerns: string[] } | null>(null);
  const [done, setDone] = useState<{ status: string; summary: string } | null>(null);
  const [agentEvents, setAgentEvents] = useState<Record<string, AgentEventEntry[]>>({});
  const [openAgent, setOpenAgent] = useState<string | null>(null);
  const [agentFull, setAgentFull] = useState(false);
  const [dagFull, setDagFull] = useState(false);
  const [forkOpen, setForkOpen] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${proto}//${window.location.host}/api/sessions/${props.sessionId}/stream`);
    wsRef.current = ws;
    ws.onmessage = (msg) => {
      try {
        const e = JSON.parse(msg.data) as SessionStatusEvent;
        setEvents((prev) => [...prev, e]);
        if (e.kind === "plan_ready") {
          const planEvent = e as unknown as { plan: { nodes: Array<{ id: string; executorType: string; dependsOn: string[] }> } };
          setDagNodes(
            planEvent.plan.nodes.map((n) => ({
              id: n.id,
              status: "pending",
              executorType: n.executorType,
              dependsOn: n.dependsOn,
            })),
          );
        } else if (e.kind === "node_status") {
          const ne = e as unknown as { nodeId: string; status: string };
          setDagNodes((prev) => {
            const i = prev.findIndex((n) => n.id === ne.nodeId);
            if (i < 0) return [...prev, { id: ne.nodeId, status: ne.status }];
            const copy = prev.slice();
            copy[i] = { ...copy[i]!, status: ne.status };
            return copy;
          });
        } else if (e.kind === "agent_event") {
          const ae = e as unknown as { nodeId: string; level: string; message: string; time: string; data?: Record<string, unknown> };
          setAgentEvents((prev) => {
            const list = prev[ae.nodeId] ?? [];
            const next: AgentEventEntry = ae.data
              ? { level: ae.level, message: ae.message, time: ae.time, data: ae.data }
              : { level: ae.level, message: ae.message, time: ae.time };
            return { ...prev, [ae.nodeId]: [...list, next] };
          });
        } else if (e.kind === "drift_warning") {
          const d = e as unknown as { drift: number; concerns: string[] };
          setDrift({ score: d.drift, concerns: d.concerns });
        } else if (e.kind === "session_done") {
          const d = e as unknown as { status: string; summary: string };
          setDone({ status: d.status, summary: d.summary });
        }
      } catch {
        // ignore malformed lines
      }
    };
    return () => {
      ws.close();
    };
  }, [props.sessionId]);

  const activeWork = useMemo(() => {
    const running = dagNodes.find((n) => n.status === "running");
    if (running) {
      const list = agentEvents[running.id] ?? [];
      return {
        node: running,
        entry: list.length > 0 ? list[list.length - 1] : null,
      };
    }
    let latest: { node: DagNode; entry: AgentEventEntry } | null = null;
    for (const node of dagNodes) {
      const list = agentEvents[node.id] ?? [];
      const entry = list[list.length - 1];
      if (!entry) continue;
      if (!latest || entry.time > latest.entry.time) latest = { node, entry };
    }
    return latest;
  }, [agentEvents, dagNodes]);

  return (
    <div className="shell">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 16 }}>
        <button className="ghost" onClick={props.onBack}>← Sessions</button>
        <code className="muted">{props.sessionId}</code>
      </div>
      {done && (
        <div className={`card ${done.status === "done" ? "ok" : done.status === "warned" ? "warn" : "error"}`}>
          <strong>Session {done.status}.</strong> {done.summary}
          {(done.status === "done" || done.status === "warned") && (
            <div className="row-flex" style={{ marginTop: 12, gap: 14, flexWrap: "wrap" }}>
              <a
                href={`/api/sessions/${props.sessionId}/download`}
                download
                style={{ color: "var(--accent)", fontWeight: 600 }}
              >
                ⬇ Download artifacts (.tar.gz)
              </a>
              <button
                className="ghost"
                onClick={() => setForkOpen(true)}
                style={{ minHeight: 0, padding: "4px 12px", fontSize: 13 }}
                title="Copy this session's folder + start a new run with same/edited request"
              >
                ⑂ Fork &amp; iterate
              </button>
            </div>
          )}
        </div>
      )}
      {drift && drift.score > 0.5 && (
        <div className="card warn">
          <strong>Drift {drift.score.toFixed(2)}:</strong>
          <ul>{drift.concerns.map((c, i) => <li key={i}>{c}</li>)}</ul>
        </div>
      )}
      {activeWork && !done && (
        <div className="card active-work">
          <div className="row" style={{ justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
            <div style={{ minWidth: 0 }}>
              <div className="muted" style={{ fontSize: 12, marginBottom: 3 }}>Active work</div>
              <div style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {activeWork.node.id}
                {activeWork.node.executorType ? <span className="muted" style={{ fontWeight: 400 }}> · {activeWork.node.executorType}</span> : null}
                <span className="muted" style={{ fontWeight: 400 }}> · {activeWork.node.status}</span>
              </div>
              <div style={{ marginTop: 4, overflowWrap: "anywhere" }}>
                {activeWork.entry ? (
                  <>
                    <strong>{activeWork.entry.level.replace("_", " ")}:</strong> {activeWork.entry.message}
                    {activeWork.entry.data?.model ? <span className="muted"> · {String(activeWork.entry.data.model)}</span> : null}
                  </>
                ) : (
                  <span className="muted">Agent started; waiting for first event.</span>
                )}
              </div>
            </div>
            <button className="ghost" onClick={() => { setOpenAgent(activeWork.node.id); setAgentFull(false); }}>
              Open log
            </button>
          </div>
        </div>
      )}
      <div className="card">
        <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>DAG</h2>
          {dagNodes.length > 0 && (
            <button className="ghost" onClick={() => setDagFull(true)}>⛶ Fullscreen</button>
          )}
        </div>
        {dagNodes.length === 0 ? <div className="muted">Awaiting plan…</div> : (
          <DagFlow nodes={dagNodes} onNodeClick={(id) => { setOpenAgent(id); setAgentFull(false); }} />
        )}
      </div>
      {dagFull && (
        <DagFullscreen
          nodes={dagNodes}
          onClose={() => setDagFull(false)}
          onNodeClick={(id) => { setOpenAgent(id); setAgentFull(false); }}
        />
      )}
      {openAgent && (
        <AgentPanel
          nodeId={openAgent}
          status={dagNodes.find((n) => n.id === openAgent)?.status ?? "?"}
          executorType={dagNodes.find((n) => n.id === openAgent)?.executorType}
          events={agentEvents[openAgent] ?? []}
          fullscreen={agentFull}
          onToggleFullscreen={() => setAgentFull((v) => !v)}
          onClose={() => { setOpenAgent(null); setAgentFull(false); }}
        />
      )}
      <FilesBrowser sessionId={props.sessionId} pollOnLive={!done} />
      <div className="card">
        <h2>Event stream</h2>
        <div className="event-stream">
          {events.map((e, i) => (
            <div key={i} className="event-line">
              <span className="event-kind">{e.kind}</span> {JSON.stringify(e).slice(0, 200)}
            </div>
          ))}
        </div>
      </div>
      {forkOpen && (
        <ForkDialog
          sessionId={props.sessionId}
          permissionMode={props.permissionMode}
          onClose={() => setForkOpen(false)}
          onForked={(newId, mode) => {
            setForkOpen(false);
            props.onSessionStarted?.(newId, mode);
          }}
        />
      )}
      <StatusBar permissionMode={props.permissionMode} broker="moonshot · deepseek" />
    </div>
  );
}

/**
 * Layered DAG layout via reactflow. We compute depth (longest path from a
 * root) for each node, then place nodes in vertical bands by depth and
 * spread horizontally within each band. Edges follow dependsOn.
 *
 * Click a node → caller's onNodeClick fires with the node id; the Stream
 * view uses that to open the floating AgentPanel for live per-node logs.
 */
function DagFlow(props: { nodes: DagNode[]; onNodeClick?: (id: string) => void; height?: number }) {
  const { rfNodes, rfEdges } = useMemo(() => {
    const idToNode = new Map<string, DagNode>();
    for (const n of props.nodes) idToNode.set(n.id, n);
    const depth = new Map<string, number>();
    const computeDepth = (id: string, seen = new Set<string>()): number => {
      if (depth.has(id)) return depth.get(id)!;
      if (seen.has(id)) return 0;
      seen.add(id);
      const node = idToNode.get(id);
      const deps = node?.dependsOn ?? [];
      const d = deps.length === 0 ? 0 : Math.max(...deps.map((p) => computeDepth(p, seen) + 1));
      depth.set(id, d);
      return d;
    };
    for (const n of props.nodes) computeDepth(n.id);
    const byDepth = new Map<number, DagNode[]>();
    for (const n of props.nodes) {
      const d = depth.get(n.id) ?? 0;
      const arr = byDepth.get(d) ?? [];
      arr.push(n);
      byDepth.set(d, arr);
    }
    const colorFor = (status: string): string => {
      if (status === "done") return "var(--ok)";
      if (status === "warned") return "var(--warn)";
      if (status === "failed") return "var(--danger)";
      if (status === "running") return "var(--accent)";
      return "var(--border)";
    };
    const rfNodes: Node[] = [];
    for (const [d, arr] of byDepth) {
      arr.forEach((n, i) => {
        rfNodes.push({
          id: n.id,
          position: { x: i * 220, y: d * 110 },
          data: { label: `${n.id}\n[${n.executorType ?? "?"}] ${n.status}` },
          style: {
            border: `2px solid ${colorFor(n.status)}`,
            background: "var(--surface-2)",
            color: "var(--text)",
            padding: 10,
            borderRadius: 6,
            whiteSpace: "pre-line",
            fontSize: 12,
            width: 200,
          },
        });
      });
    }
    const rfEdges: Edge[] = [];
    for (const n of props.nodes) {
      for (const dep of n.dependsOn ?? []) {
        rfEdges.push({ id: `${dep}->${n.id}`, source: dep, target: n.id });
      }
    }
    return { rfNodes, rfEdges };
  }, [props.nodes]);

  return (
    <div style={{ height: props.height ?? 420 }}>
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        fitView
        minZoom={0.3}
        maxZoom={2}
        onNodeClick={(_e, n) => props.onNodeClick?.(n.id)}
      >
        <Background />
        <Controls />
      </ReactFlow>
    </div>
  );
}

function DagFullscreen(props: { nodes: DagNode[]; onClose: () => void; onNodeClick: (id: string) => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") props.onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [props]);
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--bg)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          padding: "10px 16px",
          borderBottom: "1px solid var(--border)",
          background: "var(--surface)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <strong>DAG — Fullscreen</strong>
        <div className="row">
          <span className="muted" style={{ fontSize: 12 }}>Click a node to open its agent panel · Esc to exit</span>
          <button className="ghost" onClick={props.onClose}>✕ Close</button>
        </div>
      </div>
      <div style={{ flex: 1 }}>
        <DagFlow nodes={props.nodes} onNodeClick={props.onNodeClick} height={1} />
      </div>
    </div>
  );
}

/**
 * Floating panel for one agent's live event log. Two layouts:
 *   - default: 420×520px pinned bottom-right of the viewport.
 *   - fullscreen: covers the whole viewport, same content.
 * Header shows agent id + status; body lists every level/message/time
 * tuple the agent has emitted, newest at the bottom.
 */
function AgentPanel(props: {
  nodeId: string;
  status: string;
  executorType?: string;
  events: AgentEventEntry[];
  fullscreen: boolean;
  onToggleFullscreen: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [props.events.length]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") props.onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [props]);

  const colorFor = (level: string): string => {
    if (level === "error") return "var(--danger)";
    if (level === "warn") return "var(--warn)";
    if (level === "tool" || level === "tool_request") return "var(--accent)";
    if (level === "tool_result") return "var(--ok)";
    if (level === "llm_start" || level === "llm_wait") return "var(--accent)";
    if (level === "llm_done") return "var(--ok)";
    if (level === "turn") return "var(--text)";
    if (level === "complete") return "var(--ok)";
    if (level === "lifecycle") return "var(--muted)";
    return "var(--text)";
  };

  return (
    <div className={`agent-panel${props.fullscreen ? " fullscreen" : ""}`}>
      <div
        style={{
          padding: "10px 14px",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {props.nodeId}
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            {props.executorType ?? "?"} · {props.status} · {props.events.length} events
          </div>
        </div>
        <div className="row" style={{ gap: 6 }}>
          <button className="ghost" onClick={props.onToggleFullscreen} title={props.fullscreen ? "Exit fullscreen" : "Fullscreen"}>
            {props.fullscreen ? "⤢" : "⛶"}
          </button>
          <button className="ghost" onClick={props.onClose} title="Close">✕</button>
        </div>
      </div>
      <div ref={ref} style={{ flex: 1, overflowY: "auto", padding: "10px 14px", fontFamily: "ui-monospace, Cascadia Code, Consolas, monospace", fontSize: 12 }}>
        {props.events.length === 0 ? (
          <div className="muted">No events yet — agent has not started or is still initialising.</div>
        ) : (
          props.events.map((e, i) => (
            <AgentEventRow key={i} entry={e} colorFor={colorFor} />
          ))
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Files browser — view files the agents wrote to the session folder, in
// the UI, without downloading the .tar.gz.
// ---------------------------------------------------------------------------
interface TreeEntry { path: string; kind: "dir" | "file"; size?: number }

function FilesBrowser(props: { sessionId: string; pollOnLive: boolean }) {
  const [entries, setEntries] = useState<TreeEntry[]>([]);
  const [openPath, setOpenPath] = useState<string | null>(null);
  const [content, setContent] = useState<{ kind: "text"; text: string; size: number } | { kind: "image"; url: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = () => {
    fetch(`/api/sessions/${props.sessionId}/tree`, { credentials: "include" })
      .then((r) => r.json())
      .then((j) => { if (j?.ok && Array.isArray(j.entries)) setEntries(j.entries); })
      .catch(() => {});
  };

  useEffect(() => {
    refresh();
    if (!props.pollOnLive) return;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.sessionId, props.pollOnLive]);

  function selectFile(path: string) {
    setOpenPath(path);
    setError(null);
    setContent(null);
    const ext = (path.split(".").pop() ?? "").toLowerCase();
    const isImage = ["png", "jpg", "jpeg", "gif", "svg", "webp", "ico"].includes(ext);
    if (isImage) {
      // Browser fetches the binary directly from the same endpoint.
      setContent({ kind: "image", url: `/api/sessions/${props.sessionId}/file?path=${encodeURIComponent(path)}` });
      return;
    }
    setLoading(true);
    fetch(`/api/sessions/${props.sessionId}/file?path=${encodeURIComponent(path)}`, { credentials: "include" })
      .then((r) => r.json())
      .then((j) => {
        if (!j?.ok) { setError(j?.error || "Failed to load file"); return; }
        setContent({ kind: "text", text: j.text ?? "", size: j.size ?? 0 });
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }

  // Group entries by directory for a simple two-level display.
  const files = entries.filter((e) => e.kind === "file");

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Files {entries.length > 0 && <span className="muted" style={{ fontSize: 12, fontWeight: 400 }}>({files.length} files)</span>}</h2>
        <button className="ghost" onClick={refresh} title="Refresh tree" style={{ minHeight: 0, padding: "4px 12px", fontSize: 12 }}>↻ Refresh</button>
      </div>
      {entries.length === 0 ? (
        <div className="muted">{props.pollOnLive ? "Waiting for agents to write files…" : "No files."}</div>
      ) : (
        <div className="files-pane">
          <div className="files-tree">
            {files.slice(0, 500).map((e) => (
              <button
                key={e.path}
                className={`files-entry ${openPath === e.path ? "is-active" : ""}`}
                onClick={() => selectFile(e.path)}
                title={`${e.path} (${(e.size ?? 0).toLocaleString()} bytes)`}
              >
                <span aria-hidden="true">📄</span>
                <span className="files-entry-path">{e.path}</span>
                <span className="files-entry-size muted">{formatBytes(e.size ?? 0)}</span>
              </button>
            ))}
            {files.length > 500 && (
              <div className="muted" style={{ fontSize: 11, padding: 8 }}>… and {files.length - 500} more (download .tar.gz to see all).</div>
            )}
          </div>
          <div className="files-viewer">
            {!openPath && <div className="muted" style={{ padding: 12 }}>Pick a file on the left to preview.</div>}
            {openPath && error && <div className="error" style={{ padding: 12 }}>{error}</div>}
            {openPath && loading && <div className="muted" style={{ padding: 12 }}>Loading…</div>}
            {openPath && content?.kind === "text" && (
              <>
                <div className="files-viewer-bar mono">
                  <span>{openPath}</span>
                  <span className="muted">{content.size.toLocaleString()} bytes</span>
                </div>
                <pre className="files-viewer-text">{content.text}</pre>
              </>
            )}
            {openPath && content?.kind === "image" && (
              <>
                <div className="files-viewer-bar mono"><span>{openPath}</span></div>
                <div style={{ padding: 12, textAlign: "center" }}>
                  <img src={content.url} alt={openPath} style={{ maxWidth: "100%", maxHeight: "60vh", border: "1px solid var(--border)", borderRadius: 4 }} />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Fork dialog — copy the current session folder + start a new run with
// the same or edited request. Lets the user iterate without manual file ops.
// ---------------------------------------------------------------------------
function ForkDialog(props: { sessionId: string; permissionMode: string; onClose: () => void; onForked: (newId: string, mode: string) => void }) {
  const [folder, setFolder] = useState("");
  const [request, setRequest] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { permissionMode: props.permissionMode };
      if (folder) body.folder = folder;
      if (request) body.request = request;
      const r = await fetch(`/api/sessions/${props.sessionId}/fork`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = (await r.json()) as { ok: boolean; sessionId?: string; permissionMode?: string; error?: string };
      if (!j.ok || !j.sessionId) {
        setError(j.error ?? "Fork failed");
        return;
      }
      props.onForked(j.sessionId, j.permissionMode ?? props.permissionMode);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-cover" role="dialog" aria-modal="true">
      <form className="modal-card col" onSubmit={submit}>
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h2 style={{ margin: 0 }}>Fork session</h2>
          <button type="button" className="ghost" onClick={props.onClose}>✕</button>
        </div>
        <div className="muted" style={{ fontSize: 12.5 }}>
          Copies the session folder (excluding <code>.drchief2</code> runtime metadata) and starts a fresh run. The original session is untouched.
        </div>
        <label>New project name <small className="muted">(optional — defaults to <code>&lt;original&gt;-fork-...</code>)</small></label>
        <input value={folder} onChange={(e) => setFolder(e.target.value)} placeholder="my-todo-app-fork" />
        <label>New request <small className="muted">(optional — defaults to original)</small></label>
        <textarea value={request} onChange={(e) => setRequest(e.target.value)} rows={4} placeholder="Add server-sent events for live updates…" />
        {error && <div className="error" style={{ fontSize: 12 }}>{error}</div>}
        <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="ghost" onClick={props.onClose} disabled={busy}>Cancel</button>
          <button type="submit" disabled={busy}>{busy ? "Forking…" : "Fork &amp; start"}</button>
        </div>
      </form>
    </div>
  );
}
